import React, { useMemo } from "react";

import { faCheckSquare } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import { NoteBlockStatus } from "~/actionItems/enum";

import ActionItemCompletionMenu, { type AssigneeCompletionData } from "./ActionItemCompletionMenu";

import styles from "./ActionItemProgressPill.module.css";

interface ActionItemProgressPillProps {
	assignees: Array<AssigneeCompletionData>;
	showAssignYourself?: boolean;
	onToggle: (assignee: AssigneeCompletionData) => void;
	onAssignYourself?: () => void;
	className?: string;
	readonlyWithLongText?: boolean;
}

const radius = 5;
const circumference = radius * 2 * Math.PI;

export default function ActionItemProgressPill({
	assignees,
	showAssignYourself,
	onToggle,
	onAssignYourself,
	className,
	readonlyWithLongText,
}: ActionItemProgressPillProps) {
	const [completed, total] = useMemo(() => {
		let completedSum = 0;
		let totalSum = 0;
		assignees.forEach(assignee => {
			if (assignee.status === NoteBlockStatus.WONT_DO) return;
			if (assignee.status === NoteBlockStatus.DONE) completedSum++;
			totalSum++;
		});
		return [completedSum, totalSum];
	}, [assignees]);

	const filledCircleStyles = useMemo(() => {
		// We never want to show the progress circle less than 10% filled
		const progress = Math.max(total > 0 ? completed / total : 1, 0.1);
		const strokeDashoffset = circumference - progress * circumference;
		return {
			strokeDasharray: circumference,
			strokeDashoffset,
		};
	}, [completed, total]);

	const trigger = (
		<button className={cx(styles.pill, className)} title={`${Math.ceil((completed / total) * 100)}%`}>
			{readonlyWithLongText ? (
				<FontAwesomeIcon icon={faCheckSquare} />
			) : (
				<svg className={styles.progress}>
					{/* Negative cx because we rotate the whole thing 270 degrees */}
					<circle r={radius} cx="-6" cy="6" className={styles.progressBase} />
					<circle r={radius} cx="-6" cy="6" style={filledCircleStyles} className={styles.progressFilled} />
				</svg>
			)}
			{completed}/{total}
			{readonlyWithLongText && " completed"}
		</button>
	);

	return (
		<>
			<ActionItemCompletionMenu
				trigger={trigger}
				assignees={assignees}
				showAssignYourself={showAssignYourself}
				onToggle={onToggle}
				onAssignYourself={onAssignYourself}
			/>
		</>
	);
}
